import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Box, CircularProgress, Tooltip, Typography } from '@mui/material';
import React from 'react';

import { formatKpiValue, isNoData, isOffTrack, isOnTrack, KpiPerformance } from '../../util/kpiPerformance';
import { kpiColor, SparklineSVG } from './PacingSparkline';

// KpiPerformanceChips renders one contract's KPI Performance cell (ADOPS-2705):
// off-track KPIs flagged with name/value/color, an overflow chip for the ones
// that don't fit, a No-Data chip, and an On-Track chip.
// Adapted from the media-labs prototype's KpiChips, reusing OneDSP's SparklineSVG
// and the kpi_options-driven value formatting. Color uses kpiColor (one-directional:
// at/above goal stays green), not the symmetric pacing continuousColor.

// Chip packing (ADOPS-3066). The cell used to collapse off-track KPIs on their
// own count (>3), which ignored the No-Data and On-Track chips sharing the row:
// three off-track plus both summary chips is five chips, more than the two-row
// cap below can show, so the extras were silently clipped and KPIs went missing
// from the column. Chips are budgeted together instead, in slots: the column is
// ~15% of the grid width, so ~two count chips fit per row and a named off-track
// indicator (dot + KPI name + value) is about twice as wide as one. Whatever
// doesn't fit is folded into the overflow chip rather than dropped — every KPI
// is always counted by exactly one chip.
const CHIP_SLOT_BUDGET = 4;
const NAMED_CHIP_SLOTS = 2;
const SUMMARY_CHIP_SLOTS = 1;

// The slot budget above keeps the chips inside two wrapped rows, so cap the wrap
// there: a column narrowed past the budget's assumptions then clips the extra row
// cleanly (each visible chip stays whole) instead of sprawling to 3–5 lines and
// overflowing the grid row. Chip text is 0.74rem at the MUI body line-height
// (1.5); two of those plus the 2px rowGap is the cap.
const MAX_CHIP_ROWS = 2;
const CHIP_FONT_REM = 0.74;
const CHIP_LINE_HEIGHT = 1.5;
const CHIP_ROW_GAP_PX = 2; // theme spacing 0.25 → 2px (one gap between the two rows)
const CHIP_AREA_MAX_HEIGHT = `calc(${CHIP_FONT_REM}rem * ${CHIP_LINE_HEIGHT} * ${MAX_CHIP_ROWS} + ${CHIP_ROW_GAP_PX}px)`;

function KpiTooltipBody({ kpi }: { kpi: KpiPerformance }) {
  const showSparkline =
    kpi.temporal === 'daily' && !!kpi.dailyValues && kpi.dailyValues.length >= 2 && kpi.goal != null && kpi.goal !== 0; // sparkline divides by goal — avoid Infinity/NaN

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, py: 0.25, minWidth: 170 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1.5 }}>
        <Typography sx={{ fontSize: '0.74rem', fontWeight: 700, lineHeight: 1.2 }}>{kpi.name}</Typography>
        {kpi.pctOfGoal != null && (
          <Typography sx={{ fontSize: '0.68rem', opacity: 0.8 }}>{kpi.pctOfGoal.toFixed(0)}% of goal</Typography>
        )}
      </Box>
      {/* Provider directly under the KPI name (ADOPS-2797); "Not Specified" when absent. */}
      <Typography sx={{ fontSize: '0.6rem', opacity: 0.8, mt: -0.75, lineHeight: 1.1 }}>
        Provider: {kpi.provider ?? 'Not Specified'}
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', columnGap: 1.5, rowGap: 0.25 }}>
        <span style={{ fontSize: '0.7rem', opacity: 0.85 }}>Goal</span>
        <span style={{ fontSize: '0.7rem', fontWeight: 600, textAlign: 'right' }}>
          {formatKpiValue(kpi.goal, kpi.format)}
        </span>
        <span style={{ fontSize: '0.7rem', opacity: 0.85 }}>Performance</span>
        <span style={{ fontSize: '0.7rem', fontWeight: 600, textAlign: 'right' }}>
          {formatKpiValue(kpi.value, kpi.format)}
        </span>
      </Box>
      {showSparkline && (
        <Box sx={{ mt: 0.25 }}>
          <SparklineSVG
            values={kpi.dailyValues!.map((v) => v / (kpi.goal as number))}
            width={160}
            height={26}
            colorFn={kpiColor}
          />
        </Box>
      )}
    </Box>
  );
}

function OffTrackIndicator({ kpi }: { kpi: KpiPerformance }) {
  const color = kpiColor((kpi.pctOfGoal as number) / 100);

  return (
    <Tooltip title={<KpiTooltipBody kpi={kpi} />} arrow placement="top">
      <Box
        tabIndex={0}
        data-testid="kpi-offtrack"
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, lineHeight: 1.1, cursor: 'help' }}
      >
        <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
        <Typography sx={{ fontSize: '0.74rem', color, fontWeight: 500, whiteSpace: 'nowrap' }}>{kpi.name}</Typography>
        <Typography sx={{ fontSize: '0.74rem', color, fontWeight: 700, whiteSpace: 'nowrap' }}>
          {formatKpiValue(kpi.value, kpi.format)}
        </Typography>
      </Box>
    </Tooltip>
  );
}

function OverflowOffTrackChip({ kpis }: { kpis: KpiPerformance[] }) {
  const worst = Math.min(...kpis.map((k) => k.pctOfGoal as number));
  const color = kpiColor(worst / 100);
  const tooltip = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, py: 0.25 }}>
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, opacity: 0.85 }}>
        Also off-track ({kpis.length})
      </Typography>
      {kpis.map((k) => (
        <KpiTooltipBody key={k.name} kpi={k} />
      ))}
    </Box>
  );

  return (
    <Tooltip title={tooltip} arrow placement="top">
      <Box
        tabIndex={0}
        data-testid="kpi-overflow"
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, cursor: 'help', lineHeight: 1.1 }}
      >
        <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
        <Typography sx={{ fontSize: '0.72rem', color, fontWeight: 600, whiteSpace: 'nowrap' }}>
          +{kpis.length} off-track
        </Typography>
      </Box>
    </Tooltip>
  );
}

function NoDataChip({ kpis }: { kpis: KpiPerformance[] }) {
  const tooltip = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, py: 0.25 }}>
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, opacity: 0.85 }}>No data ({kpis.length})</Typography>
      {kpis.map((k) => (
        <Box key={k.name} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5 }}>
          <span style={{ fontSize: '0.7rem' }}>{k.name}</span>
          <span style={{ fontSize: '0.7rem', opacity: 0.7 }}>
            {k.goal != null ? `goal ${formatKpiValue(k.goal, k.format)}` : 'no goal'}
          </span>
        </Box>
      ))}
    </Box>
  );

  return (
    <Tooltip title={tooltip} arrow placement="top">
      <Box
        tabIndex={0}
        data-testid="kpi-nodata"
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, cursor: 'help', lineHeight: 1.1 }}
      >
        <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: 'rgba(0,0,0,0.4)', flexShrink: 0 }} />
        <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', fontWeight: 500, whiteSpace: 'nowrap' }}>
          +{kpis.length} No-Data
        </Typography>
      </Box>
    </Tooltip>
  );
}

function OnTrackChip({ kpis }: { kpis: KpiPerformance[] }) {
  const tooltip = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, py: 0.25 }}>
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, opacity: 0.85 }}>On track ({kpis.length})</Typography>
      {kpis.map((k) => (
        <KpiTooltipBody key={k.name} kpi={k} />
      ))}
    </Box>
  );

  return (
    <Tooltip title={tooltip} arrow placement="top">
      <Box
        tabIndex={0}
        data-testid="kpi-ontrack"
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, lineHeight: 1.1, cursor: 'help' }}
      >
        <CheckCircleIcon sx={{ fontSize: 12, color: '#2E7D32' }} />
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 500, color: 'text.secondary', whiteSpace: 'nowrap' }}>
          {kpis.length} On-Track
        </Typography>
      </Box>
    </Tooltip>
  );
}

export interface ChipPlan {
  visibleOffTrack: KpiPerformance[]; // named indicators, worst (lowest % of goal) first
  hiddenOffTrack: KpiPerformance[]; // folded into the "+N off-track" chip
  noData: KpiPerformance[];
  onTrack: KpiPerformance[];
}

// planChips splits a contract's KPIs into the chips the cell renders, spending
// the slot budget above: the No-Data and On-Track chips are reserved first (they
// each stand for KPIs that have no other chip), then as many named off-track
// indicators as still fit — remembering that folding any off-track KPI away costs
// a slot for the overflow chip itself. When even that leaves nothing, the
// overflow chip is rendered over budget: an off-track KPI is never dropped.
export function planChips(kpis: KpiPerformance[]): ChipPlan {
  const offTrack = kpis.filter(isOffTrack).sort((a, b) => (a.pctOfGoal as number) - (b.pctOfGoal as number));
  const onTrack = kpis.filter(isOnTrack);
  const noData = kpis.filter(isNoData);

  const reserved = (noData.length > 0 ? SUMMARY_CHIP_SLOTS : 0) + (onTrack.length > 0 ? SUMMARY_CHIP_SLOTS : 0);
  const available = CHIP_SLOT_BUDGET - reserved;

  let named = offTrack.length;
  while (named > 0 && named * NAMED_CHIP_SLOTS + (named < offTrack.length ? SUMMARY_CHIP_SLOTS : 0) > available) {
    named -= 1;
  }

  return {
    visibleOffTrack: offTrack.slice(0, named),
    hiddenOffTrack: offTrack.slice(named),
    noData,
    onTrack,
  };
}

export interface KpiPerformanceChipsProps {
  kpis: KpiPerformance[];
  loading?: boolean;
}

export default function KpiPerformanceChips({ kpis, loading = false }: KpiPerformanceChipsProps): JSX.Element | null {
  // Must precede the empty-list return: in flight there are never any kpis yet.
  if (loading) {
    return (
      // height 100% is load-bearing: without it the box shrinks to the spinner.
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: '100%',
        }}
      >
        <CircularProgress size={16} aria-label="Loading KPI performance" />
      </Box>
    );
  }
  if (!kpis || kpis.length === 0) return null;

  const { visibleOffTrack, hiddenOffTrack, noData, onTrack } = planChips(kpis);

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        columnGap: 1.5,
        rowGap: 0.25,
        alignItems: 'center',
        alignContent: 'flex-start', // pack wrapped rows from the top so the FIRST two are the visible ones
        maxHeight: CHIP_AREA_MAX_HEIGHT,
        overflow: 'hidden',
        width: '100%',
      }}
    >
      {visibleOffTrack.map((kpi) => (
        <OffTrackIndicator key={kpi.name} kpi={kpi} />
      ))}
      {hiddenOffTrack.length > 0 && <OverflowOffTrackChip kpis={hiddenOffTrack} />}
      {noData.length > 0 && <NoDataChip kpis={noData} />}
      {onTrack.length > 0 && <OnTrackChip kpis={onTrack} />}
    </Box>
  );
}
